import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, CheckCircle, XCircle, Clock, Award, 
  ChevronRight, AlertTriangle, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyQuestionario = () => {

  const { questionarioId } = useParams();
  const navigate = useNavigate();
  const [questionario, setQuestionario] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [tentativas, setTentativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  
  const token = localStorage.getItem('tayane_token');

  useEffect(() => {
    fetchQuestionario();
  }, [questionarioId]);

  const fetchQuestionario = async () => {
    try {
      const [qRes, tentativasRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/academy/questionarios/${questionarioId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/questionarios/${questionarioId}/minhas-tentativas`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setQuestionario(qRes.data);
      setTentativas(Array.isArray(tentativasRes.data) ? tentativasRes.data : (tentativasRes.data?.tentativas || tentativasRes.data?.items || tentativasRes.data?.data || []));
      
      // Se já passou, mostrar resultado
      const tentativasArr = Array.isArray(tentativasRes.data) ? tentativasRes.data : (tentativasRes.data?.tentativas || tentativasRes.data?.items || []);
      const tentativaAprovada = tentativasArr.find(t => t.aprovado);
      if (tentativaAprovada) {
        setResultado(tentativaAprovada);
        setMostrarResultado(true);
      }
    } catch (error) {
      console.error("Erro ao carregar questionário:", error);
      toast.error("Erro ao carregar questionário");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOpcao = (questaoId, opcaoIndex) => {
    if (mostrarResultado) return;
    setRespostas(prev => ({ ...prev, [questaoId]: opcaoIndex }));
  };

  const handleSubmit = async () => {
    // Verificar se todas as questões foram respondidas
    const questoesIds = questionario.questoes.map(q => q.id);
    const faltando = questoesIds.filter(id => respostas[id] === undefined);
    
    if (faltando.length > 0) {
      toast.error(`Responda todas as questões (${faltando.length} faltando)`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/academy/questionarios/${questionarioId}/responder`,
        {
          questionario_id: questionarioId,
          respostas: questoesIds.map(id => ({
            questao_id: id,
            opcao_selecionada: respostas[id]
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResultado(response.data);
      setMostrarResultado(true);
      
      if (response.data.aprovado) {
        toast.success("Parabéns! Você foi aprovado!");
      } else {
        toast.error("Não foi dessa vez. Tente novamente!");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao enviar respostas";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTentarNovamente = () => {
    setRespostas({});
    setResultado(null);
    setMostrarResultado(false);
    fetchQuestionario();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!questionario) return null;

  const podeResponder = !mostrarResultado && (
    questionario.tentativas_permitidas === 0 || 
    tentativas.length < questionario.tentativas_permitidas
  );

  const tentativasRestantes = questionario.tentativas_permitidas === 0 
    ? "Ilimitadas" 
    : questionario.tentativas_permitidas - tentativas.length;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                {questionario.titulo}
              </h1>
              {questionario.descricao && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {questionario.descricao}
                </p>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-300">
                {questionario.total_questoes} questões
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-300">
                Nota mínima: {questionario.nota_minima}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-300">
                Tentativas restantes: {tentativasRestantes}
              </span>
            </div>
          </div>

          {/* Resultado */}
          {mostrarResultado && resultado && (
            <div className={`rounded-xl p-6 ${
              resultado.aprovado 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center gap-4">
                {resultado.aprovado ? (
                  <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <Award className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                )}
                <div>
                  <h3 className={`text-xl font-bold ${
                    resultado.aprovado ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {resultado.aprovado ? 'Aprovado!' : 'Reprovado'}
                  </h3>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    Nota: {resultado.nota}%
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {resultado.acertos} de {resultado.total_questoes} acertos
                  </p>
                </div>
              </div>
              
              {!resultado.aprovado && podeResponder && (
                <button
                  onClick={handleTentarNovamente}
                  className="mt-4 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar Novamente
                </button>
              )}
            </div>
          )}

          {/* Questões */}
          <div className="space-y-4">
            {questionario.questoes.map((questao, qIndex) => {
              const respostaUsuario = respostas[questao.id];
              const resultadoQuestao = resultado?.respostas?.find(r => r.questao_id === questao.id);
              
              return (
                <div 
                  key={questao.id}
                  className={`glass-panel dark:bg-slate-900/80 rounded-xl p-5 ${
                    mostrarResultado && resultadoQuestao
                      ? resultadoQuestao.correta
                        ? 'ring-2 ring-green-400'
                        : 'ring-2 ring-red-400'
                      : ''
                  }`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {qIndex + 1}
                    </span>
                    <h3 className="font-medium text-slate-800 dark:text-slate-100">
                      {questao.pergunta}
                    </h3>
                  </div>
                  
                  <div className="space-y-2 ml-11">
                    {questao.opcoes.map((opcao, oIndex) => {
                      const isSelected = respostaUsuario === oIndex;
                      const isCorreta = mostrarResultado && resultadoQuestao?.opcao_correta === oIndex;
                      const isErrada = mostrarResultado && isSelected && !resultadoQuestao?.correta;
                      
                      return (
                        <button
                          key={oIndex}
                          onClick={() => handleSelectOpcao(questao.id, oIndex)}
                          disabled={mostrarResultado}
                          className={`w-full p-3 rounded-lg text-left transition-all flex items-center gap-3 ${
                            mostrarResultado
                              ? isCorreta
                                ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-400 text-green-800 dark:text-green-200'
                                : isErrada
                                ? 'bg-red-100 dark:bg-red-900/30 border-2 border-red-400 text-red-800 dark:text-red-200'
                                : 'bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                              : isSelected
                              ? 'bg-primary/10 border-2 border-primary text-primary'
                              : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary/50'
                          }`}
                        >
                          <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            mostrarResultado
                              ? isCorreta
                                ? 'border-green-500 bg-green-500 text-white'
                                : isErrada
                                ? 'border-red-500 bg-red-500 text-white'
                                : 'border-slate-300 dark:border-slate-600'
                              : isSelected
                              ? 'border-primary bg-primary text-white'
                              : 'border-slate-300 dark:border-slate-600'
                          }`}>
                            {mostrarResultado && isCorreta && <CheckCircle className="w-4 h-4" />}
                            {mostrarResultado && isErrada && <XCircle className="w-4 h-4" />}
                            {!mostrarResultado && isSelected && <CheckCircle className="w-4 h-4" />}
                          </span>
                          <span className="flex-1">{opcao.texto}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Explicação */}
                  {mostrarResultado && resultadoQuestao?.explicacao && (
                    <div className="mt-4 ml-11 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Explicação:</strong> {resultadoQuestao.explicacao}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Botão Enviar */}
          {podeResponder && !mostrarResultado && (
            <div className="flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Respostas
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Histórico de tentativas */}
          {tentativas.length > 0 && (
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <h3 className="font-medium text-slate-800 dark:text-slate-100 mb-3">
                Histórico de Tentativas
              </h3>
              <div className="space-y-2">
                {tentativas.map((t, i) => (
                  <div 
                    key={t.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">#{tentativas.length - i}</span>
                      <span className={`font-medium ${t.aprovado ? 'text-green-600' : 'text-red-500'}`}>
                        {t.nota}%
                      </span>
                      <span className="text-sm text-slate-500">
                        ({t.acertos}/{t.total_questoes} acertos)
                      </span>
                    </div>
                    {t.aprovado && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                        Aprovado
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcademyQuestionario;
